import React, {useEffect, useMemo, useState} from "react"
import dayjs from "dayjs"
import {Result, Select, Tooltip} from "antd"
import {useLocation} from "react-router-dom"
import {useLink, useLogout, useMenu, useNavigation, type ITreeMenu} from "@refinedev/core"
import {
    CarFront,
    ChevronDown,
    LogOut,
    Menu,
    ReceiptText,
    Search,
    Store,
    Settings,
    User,
    X
} from "../../components/icons/AppIcons"
import {changeTheme, setSelectedBranchId, useSelectedApp} from "../../features/app/appSlice"
import {useDispatch} from "../../features/store"
import {cn} from "../../lib/utils"
import {Button} from "../../components/ui/button"
import {Input} from "../../components/ui/input"
import {useGetDashboardHybridStatisticsQuery} from "../../features/dashboard/dashboardApi"
import {AccessPolicy, getScopedBranchIds, isPathAllowed, useAccessPolicy} from "../../features/access"
import {useGetAllBranchesListQuery} from "../../features/branch/branchApi"
import Loading from "../../components/loading/Loading"
import {getBranchText} from "../../features/branch/branchUtils"
import BrandLogo from "../../components/brand/BrandLogo"
import {ORDER_IDENTIFIER_MODE} from "../../utils/api"
import {useTranslation} from "react-i18next"
import LanguageSwitcher from "../../components/language/LanguageSwitcher"
import {translateUi} from "../../i18n/translateUi"
import {getAdminLanguage} from "../../utils/language"
import AdminNotificationsCenter from "../../features/notification/AdminNotificationsCenter"


interface ShadcnAdminLayoutProps {
    children: React.ReactNode
}

interface NavItem {
    key: string
    label: React.ReactNode
    icon?: React.ReactNode
    path?: string
    children?: NavItem[]
}

const HIDDEN_MENU_KEYS = new Set([
    "clients",
    "/clients",
    "team",
    "/team",
    "settings",
    "/settings",
    "settings-team",
    "settings-branches",
    "settings-shipping",
    "settings-payment-types",
    "settings-couriers",
    "settings-banners",
    "settings-reviews",
    "/settings/team",
    "/settings/branches",
    "/settings/shipping",
    "/settings/payment-types",
    "/settings/couriers",
    "/settings/banners",
    "/settings/reviews"
])

const toNavItems = (items: ITreeMenu[], policy: AccessPolicy): NavItem[] =>
    items
        .filter((item) => !HIDDEN_MENU_KEYS.has(String(item.key || item.route || "")))
        .map((item, index) => {
            const path = item.route || item.key || undefined
            const children = item.children?.length ? toNavItems(item.children, policy) : undefined
            const hasChildren = Boolean(children?.length)

            if (!hasChildren && path && !isPathAllowed(path, policy)) {
                return null
            }

            if (!path && children && children.length === 0) {
                return null
            }

            return {
                key: item.key || `nav-${index}`,
                label: item.label || translateUi("Раздел"),
                icon: item.icon,
                path,
                children
            }
        })
        .filter(Boolean) as NavItem[]

const sectionLabel = (label: React.ReactNode) =>
    typeof label === "string" ? label : translateUi("Раздел")

const SidebarHelpIcon = () => (
    <svg className="admin-sidebar__utility-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M18.3337 10.0003C18.3337 14.6027 14.6027 18.3337 10.0003 18.3337C5.39795 18.3337 1.66699 14.6027 1.66699 10.0003C1.66699 5.39795 5.39795 1.66699 10.0003 1.66699C14.6027 1.66699 18.3337 5.39795 18.3337 10.0003ZM10.0003 6.45866C9.42503 6.45866 8.95866 6.92503 8.95866 7.50033C8.95866 7.8455 8.67884 8.12533 8.33366 8.12533C7.98848 8.12533 7.70866 7.8455 7.70866 7.50033C7.70866 6.23467 8.73467 5.20866 10.0003 5.20866C11.266 5.20866 12.292 6.23467 12.292 7.50033C12.292 7.95561 12.1586 8.38164 11.9285 8.73919C11.7844 8.96314 11.6159 9.17694 11.4606 9.37002L11.3768 9.47387C11.2479 9.63341 11.1296 9.77986 11.0202 9.93014C10.7506 10.3007 10.6253 10.5792 10.6253 10.8337V11.2503C10.6253 11.5955 10.3455 11.8753 10.0003 11.8753C9.65515 11.8753 9.37533 11.5955 9.37533 11.2503V10.8337C9.37533 10.1676 9.70234 9.6168 10.0095 9.1947C10.1392 9.01635 10.2814 8.84044 10.4107 8.68049L10.4867 8.5864C10.641 8.3946 10.7724 8.22587 10.8773 8.06278C10.9815 7.9009 11.042 7.70863 11.042 7.50033C11.042 6.92503 10.5756 6.45866 10.0003 6.45866ZM10.6253 13.3337C10.6253 12.9885 10.3455 12.7087 10.0003 12.7087C9.65515 12.7087 9.37533 12.9885 9.37533 13.3337V14.167C9.37533 14.5122 9.65515 14.792 10.0003 14.792C10.3455 14.792 10.6253 14.5122 10.6253 14.167V13.3337Z" fill="currentColor" />
    </svg>
)

const SidebarExternalIcon = () => (
    <svg className="admin-sidebar__external-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M7.5 5.625C7.15482 5.625 6.875 5.34518 6.875 5C6.875 4.65482 7.15482 4.375 7.5 4.375H15C15.3452 4.375 15.625 4.65482 15.625 5V12.5C15.625 12.8452 15.3452 13.125 15 13.125C14.6548 13.125 14.375 12.8452 14.375 12.5V6.50888L5.44194 15.4419C5.19786 15.686 4.80214 15.686 4.55806 15.4419C4.31398 15.1979 4.31398 14.8021 4.55806 14.5581L13.4911 5.625H7.5Z" fill="currentColor" />
    </svg>
)

const SidebarSettingsIcon = () => (
    <svg className="admin-sidebar__utility-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M11.899 1.79386C11.5904 1.66699 11.1991 1.66699 10.4167 1.66699C9.63418 1.66699 9.24294 1.66699 8.93433 1.79386C8.52284 1.96302 8.19591 2.28747 8.02546 2.69585C7.94766 2.88228 7.91721 3.09908 7.90529 3.41532C7.88778 3.88006 7.64763 4.31024 7.2418 4.54277C6.83598 4.7753 6.34053 4.76661 5.92624 4.54929C5.64432 4.40141 5.43991 4.31918 5.23832 4.29284C4.79674 4.23514 4.35015 4.3539 3.9968 4.62299C3.73178 4.82481 3.53616 5.16107 3.14492 5.8336C2.75368 6.50613 2.55806 6.84239 2.51446 7.17108C2.45632 7.60933 2.57598 8.05254 2.84712 8.40323C2.97088 8.56329 3.14481 8.69785 3.41475 8.86618C3.8116 9.11365 4.06693 9.53521 4.06691 10.0003C4.06688 10.4654 3.81155 10.8869 3.41475 11.1344C3.14476 11.3027 2.97081 11.4373 2.84704 11.5974C2.5759 11.9481 2.45624 12.3913 2.51437 12.8295C2.55798 13.1582 2.7536 13.4945 3.14484 14.167C3.53608 14.8395 3.7317 15.1758 3.99671 15.3776C4.35007 15.6467 4.79666 15.7655 5.23824 15.7078C5.43981 15.6814 5.64421 15.5992 5.92611 15.4513C6.34043 15.234 6.83592 15.2253 7.24177 15.4579C7.64762 15.6904 7.88778 16.1206 7.90529 16.5854C7.91721 16.9016 7.94766 17.1184 8.02546 17.3048C8.19591 17.7132 8.52284 18.0376 8.93433 18.2068C9.24294 18.3337 9.63418 18.3337 10.4167 18.3337C11.1991 18.3337 11.5904 18.3337 11.899 18.2068C12.3105 18.0376 12.6374 17.7132 12.8079 17.3048C12.8857 17.1184 12.9161 16.9016 12.928 16.5853C12.9456 16.1206 13.1857 15.6904 13.5915 15.4579C13.9973 15.2253 14.4928 15.234 14.9072 15.4513C15.1891 15.5991 15.3934 15.6814 15.595 15.7077C16.0366 15.7654 16.4832 15.6466 16.8365 15.3775C17.1016 15.1757 17.2972 14.8395 17.6884 14.1669C18.0797 13.4944 18.2753 13.1581 18.3189 12.8295C18.377 12.3912 18.2573 11.948 17.9862 11.5973C17.8624 11.4372 17.6885 11.3027 17.4185 11.1343C17.0217 10.8869 16.7664 10.4654 16.7664 10.0003C16.7664 9.5352 17.0218 9.11373 17.4185 8.86632C17.6885 8.69795 17.8625 8.56337 17.9863 8.40328C18.2574 8.0526 18.3771 7.60938 18.319 7.17114C18.2754 6.84245 18.0797 6.50619 17.6885 5.83366C17.2973 5.16113 17.1016 4.82487 16.8366 4.62305C16.4833 4.35396 16.0367 4.2352 15.5951 4.2929C15.3935 4.31923 15.1891 4.40146 14.9072 4.54932C14.4929 4.76665 13.9974 4.77534 13.5916 4.54279C13.1857 4.31025 12.9456 3.88004 12.928 3.41527C12.9161 3.09906 12.8857 2.88227 12.8079 2.69585C12.6374 2.28747 12.3105 1.96302 11.899 1.79386ZM10.4167 12.5003C11.8079 12.5003 12.9357 11.381 12.9357 10.0003C12.9357 8.61961 11.8079 7.50033 10.4167 7.50033C9.02544 7.50033 7.89763 8.61961 7.89763 10.0003C7.89763 11.381 9.02544 12.5003 10.4167 12.5003Z" fill="currentColor" />
    </svg>
)

const SidebarLogoutIcon = () => (
    <svg className="admin-sidebar__utility-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M13.4378 9.99967C13.4378 9.6545 13.158 9.37467 12.8128 9.37467L3.66903 9.37467L5.3029 7.97421C5.56498 7.74957 5.59533 7.35501 5.37069 7.09293C5.14606 6.83085 4.75149 6.8005 4.48941 7.02514L1.57275 9.52514C1.43422 9.64388 1.35449 9.81722 1.35449 9.99967C1.35449 10.1821 1.43422 10.3555 1.57275 10.4742L4.48941 12.9742C4.75149 13.1988 5.14606 13.1685 5.37069 12.9064C5.59533 12.6443 5.56498 12.2498 5.3029 12.0251L3.66903 10.6247L12.8128 10.6247C13.158 10.6247 13.4378 10.3449 13.4378 9.99967Z" fill="currentColor" />
        <path d="M7.81282 6.66634C7.81282 7.2515 7.81282 7.54408 7.95325 7.75426C8.01406 7.84527 8.0922 7.92341 8.18321 7.98421C8.39338 8.12464 8.68596 8.12464 9.27112 8.12464L12.8128 8.12464C13.8484 8.12464 14.6878 8.96411 14.6878 9.99964C14.6878 11.0352 13.8484 11.8746 12.8128 11.8746L9.27119 11.8746C8.68597 11.8746 8.39335 11.8746 8.18316 12.0151C8.09219 12.0759 8.01407 12.154 7.95328 12.245C7.81282 12.4552 7.81282 12.7478 7.81282 13.333C7.81283 15.69 7.81283 16.8685 8.54506 17.6008C9.27729 18.333 10.4556 18.333 12.8126 18.333L13.646 18.333C16.003 18.333 17.1815 18.333 17.9137 17.6008C18.646 16.8685 18.646 15.69 18.646 13.333L18.646 6.66634C18.646 4.30933 18.646 3.1308 17.9137 2.39857C17.1815 1.66634 16.003 1.66634 13.646 1.66634L12.8126 1.66634C10.4556 1.66634 9.27729 1.66634 8.54506 2.39857C7.81282 3.13081 7.81282 4.30932 7.81282 6.66634Z" fill="currentColor" />
    </svg>
)

const HeaderLightThemeIcon = () => (
    <svg className="h-4 w-4" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.333 7.99967C11.333 9.84062 9.84062 11.333 7.99967 11.333C6.15872 11.333 4.66634 9.84062 4.66634 7.99967C4.66634 6.15872 6.15872 4.66634 7.99967 4.66634C9.84062 4.66634 11.333 6.15872 11.333 7.99967Z" fill="#636363" />
        <path fillRule="evenodd" clipRule="evenodd" d="M7.99967 0.833008C8.27582 0.833008 8.49967 1.05687 8.49967 1.33301V2.66634C8.49967 2.94248 8.27582 3.16634 7.99967 3.16634C7.72353 3.16634 7.49967 2.94248 7.49967 2.66634V1.33301C7.49967 1.05687 7.72353 0.833008 7.99967 0.833008ZM2.44544 2.47707C2.63177 2.27327 2.94804 2.25911 3.15184 2.44544L4.63318 3.79983C4.83698 3.98617 4.85114 4.30243 4.66481 4.50623C4.47847 4.71003 4.16221 4.72419 3.95841 4.53786L2.47707 3.18347C2.27327 2.99713 2.25911 2.68087 2.44544 2.47707ZM13.5539 2.47707C13.7402 2.68087 13.7261 2.99713 13.5223 3.18347L12.0409 4.53786C11.8371 4.72419 11.5209 4.71003 11.3345 4.50623C11.1482 4.30243 11.1624 3.98617 11.3662 3.79983L12.8475 2.44544C13.0513 2.25911 13.3676 2.27327 13.5539 2.47707ZM0.833008 7.99967C0.833008 7.72353 1.05687 7.49967 1.33301 7.49967H2.66634C2.94248 7.49967 3.16634 7.72353 3.16634 7.99967C3.16634 8.27582 2.94248 8.49967 2.66634 8.49967H1.33301C1.05687 8.49967 0.833008 8.27582 0.833008 7.99967ZM12.833 7.99967C12.833 7.72353 13.0569 7.49967 13.333 7.49967H14.6663C14.9425 7.49967 15.1663 7.72353 15.1663 7.99967C15.1663 8.27582 14.9425 8.49967 14.6663 8.49967H13.333C13.0569 8.49967 12.833 8.27582 12.833 7.99967ZM11.35 11.3498C11.5453 11.1545 11.8619 11.1546 12.0571 11.3498L13.5385 12.8314C13.7337 13.0266 13.7337 13.3432 13.5384 13.5385C13.3432 13.7337 13.0266 13.7337 12.8313 13.5384L11.35 12.0569C11.1547 11.8616 11.1547 11.545 11.35 11.3498ZM4.64945 11.3499C4.84471 11.5452 4.84471 11.8617 4.64945 12.057L3.16797 13.5385C2.97271 13.7337 2.65613 13.7337 2.46086 13.5385C2.2656 13.3432 2.2656 13.0266 2.46086 12.8314L3.94235 11.3499C4.13761 11.1546 4.45419 11.1546 4.64945 11.3499ZM7.99967 12.833C8.27582 12.833 8.49967 13.0569 8.49967 13.333V14.6663C8.49967 14.9425 8.27582 15.1663 7.99967 15.1663C7.72353 15.1663 7.49967 14.9425 7.49967 14.6663V13.333C7.49967 13.0569 7.72353 12.833 7.99967 12.833Z" fill="#636363" />
    </svg>
)

const HeaderDarkThemeIcon = () => (
    <svg className="h-4 w-4" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.2671 1.5378C13.1598 1.26474 12.7747 1.26474 12.6675 1.5378L12.3805 2.26799C12.3478 2.35136 12.282 2.41735 12.199 2.45023L11.4715 2.73821C11.1994 2.8459 11.1994 3.23234 11.4715 3.34004L12.199 3.62802C12.282 3.6609 12.3478 3.72689 12.3805 3.81025L12.6675 4.54044C12.7747 4.8135 13.1598 4.8135 13.2671 4.54044L13.554 3.81025C13.5867 3.72689 13.6525 3.6609 13.7355 3.62802L14.463 3.34004C14.7351 3.23234 14.7351 2.8459 14.463 2.73821L13.7355 2.45023C13.6525 2.41735 13.5867 2.35136 13.554 2.26799L13.2671 1.5378Z" fill="#A980FF" />
        <path d="M10.6888 5.41946C10.5815 5.1464 10.1965 5.1464 10.0892 5.41946L9.98461 5.68569C9.95185 5.76905 9.8861 5.83504 9.80304 5.86792L9.53779 5.97292C9.26574 6.08061 9.26574 6.46706 9.53779 6.57475L9.80304 6.67975C9.8861 6.71263 9.95185 6.77862 9.98461 6.86198L10.0892 7.12822C10.1965 7.40127 10.5815 7.40127 10.6888 7.12822L10.7934 6.86198C10.8262 6.77862 10.8919 6.71263 10.975 6.67975L11.2403 6.57475C11.5123 6.46706 11.5123 6.08061 11.2403 5.97292L10.975 5.86792C10.8919 5.83504 10.8262 5.76905 10.7934 5.68569L10.6888 5.41946Z" fill="#A980FF" />
        <path d="M7.99967 14.6663C11.6816 14.6663 14.6663 11.6816 14.6663 7.99967C14.6663 7.69121 14.204 7.64023 14.0445 7.90424C13.2856 9.16009 11.9074 9.99967 10.333 9.99967C7.93977 9.99967 5.99967 8.05958 5.99967 5.66634C5.99967 4.09198 6.83926 2.71371 8.09511 1.95485C8.35912 1.79532 8.30814 1.33301 7.99967 1.33301C4.31778 1.33301 1.33301 4.31778 1.33301 7.99967C1.33301 11.6816 4.31778 14.6663 7.99967 14.6663Z" fill="#A980FF" />
    </svg>
)

const HeaderNotificationIcon = () => (
    <svg className="h-4 w-4" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.56786 13.4952C6.12859 14.208 7.00946 14.6673 8 14.6673C8.99055 14.6673 9.87142 14.208 10.4321 13.4952C8.81762 13.714 7.18238 13.714 5.56786 13.4952Z" fill="#636363" />
        <path d="M12.4994 6.00065V6.47005C12.4994 7.03338 12.6602 7.5841 12.9615 8.05282L13.6998 9.20142C14.3741 10.2506 13.8593 11.6766 12.6864 12.0084C9.61817 12.8763 6.38183 12.8763 3.31357 12.0084C2.1407 11.6766 1.62588 10.2506 2.30024 9.20142L3.03853 8.05282C3.33981 7.5841 3.50058 7.03338 3.50058 6.47005V6.00065C3.50058 3.42332 5.51504 1.33398 8 1.33398C10.485 1.33398 12.4994 3.42332 12.4994 6.00065Z" fill="#636363" />
    </svg>
)

export const ShadcnAdminLayout: React.FC<ShadcnAdminLayoutProps> = ({children}) => {
    const {t} = useTranslation()
    const dispatch = useDispatch()
    const app = useSelectedApp()
    const location = useLocation()
    const Link = useLink()
    const {push} = useNavigation()
    const {menuItems} = useMenu()
    const {mutate: logoutAction} = useLogout()
    const accessPolicy = useAccessPolicy()
    const {selectedBranchId} = app
    const [mobileOpen, setMobileOpen] = useState(false)
    const [profileOpen, setProfileOpen] = useState(false)
    const [quickLookupValue, setQuickLookupValue] = useState("")
    const {data: branchesData, isLoading: isBranchesLoading} = useGetAllBranchesListQuery(
        {lang: getAdminLanguage()},
        {refetchOnMountOrArgChange: true, skip: !accessPolicy.canSelectBranches}
    )

    const navItems = useMemo(() => toNavItems(menuItems, accessPolicy), [accessPolicy, menuItems])
    const todayHybridQueryParams = useMemo(() => ({
        lang: getAdminLanguage(),
        date_from: dayjs().startOf("day").toISOString(),
        date_to: dayjs().endOf("day").toISOString(),
        branch_id_list: getScopedBranchIds(accessPolicy, undefined, selectedBranchId)
    }), [accessPolicy, selectedBranchId])
    const {data: liveDashboardData} = useGetDashboardHybridStatisticsQuery(todayHybridQueryParams, {
        refetchOnMountOrArgChange: true,
        pollingInterval: 300_000,
        skip: !accessPolicy.canReadOrders && !accessPolicy.canReadCouriers
    })
    const liveOrdersCount = liveDashboardData?.data?.orders.active_order_count ?? 0
    const liveCouriersCount = liveDashboardData?.data?.couriers.active_courier_count ?? 0
    const headerUserName = accessPolicy.user?.name || t("navigation.profile")
    const branchOptions = useMemo(
        () => (branchesData?.data?.content || []).map((branch) => ({
            label: getBranchText(branch.name, branch.id),
            value: branch.id
        })),
        [branchesData?.data?.content]
    )
    const quickQueryFromLocation = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search])
    const hasSelectedBranch = Boolean(selectedBranchId)
    const hasQuickLookup = quickLookupValue.trim().length > 0
    const quickLookupPlaceholder = ORDER_IDENTIFIER_MODE === "external_number"
        ? t("header.externalOrderOrPhone")
        : t("header.orderOrPhone")

    useEffect(() => {
        setQuickLookupValue(quickQueryFromLocation)
    }, [quickQueryFromLocation])

    const submitQuickLookup = () => {
        const normalized = quickLookupValue.trim()
        if (!normalized) {
            push("/orders")
            return
        }
        push(`/orders?q=${encodeURIComponent(normalized)}`)
    }

    const clearQuickLookup = () => {
        setQuickLookupValue("")
        if (quickQueryFromLocation && location.pathname.startsWith("/orders")) {
            push("/orders")
        }
    }

    const navigateFromSidebar = (path: string) => {
        setMobileOpen(false)
        push(path)
    }

    const isNavPathActive = (path?: string, exact = false) => Boolean(
        path && (exact ? location.pathname === path : (location.pathname === path || location.pathname.startsWith(`${path}/`)))
    )
    const hasActiveChild = (items?: NavItem[]): boolean =>
        Boolean(items?.some((child) => isNavPathActive(child.path, child.path === "/orders") || hasActiveChild(child.children)))

    const renderNav = (items: NavItem[], nested = false) => (
        <ul className={nested ? "admin-sidebar__children" : "admin-sidebar__nav"}>
            {items.map((item) => {
                const hasChildren = Boolean(item.children?.length)
                const active = isNavPathActive(item.path, nested && item.path === "/orders")
                const childActive = hasActiveChild(item.children) || (hasChildren && isNavPathActive(item.path))
                const firstChildPath = item.children?.find((child) => child.path)?.path
                const content = (
                    <span
                        className={cn(
                            "admin-sidebar__item",
                            nested && "admin-sidebar__child-link",
                            active && (nested ? "admin-sidebar__child-link--active" : "admin-sidebar__item--active")
                        )}
                    >
                        <span className="admin-sidebar__item-icon">{item.icon}</span>
                        <span className="admin-sidebar__item-label">{item.label}</span>
                        {active ? <span className="admin-sidebar__active-dot" /> : null}
                    </span>
                )

                return (
                    <li key={item.key} className={cn(hasChildren && "admin-sidebar__group", childActive && "admin-sidebar__group--active")}>
                        {item.path && !hasChildren ? (
                            <Link
                                to={item.path}
                                className="admin-sidebar__link"
                                onClick={() => setMobileOpen(false)}
                                aria-current={active ? "page" : undefined}
                            >
                                {content}
                            </Link>
                        ) : (
                            <div>
                                <button
                                    type="button"
                                    className={cn("admin-sidebar__item admin-sidebar__item-button", childActive && "admin-sidebar__item--active-parent")}
                                    onClick={() => firstChildPath && navigateFromSidebar(firstChildPath)}
                                >
                                    <span className="admin-sidebar__item-icon">{item.icon}</span>
                                    <span className="admin-sidebar__item-label">{sectionLabel(item.label)}</span>
                                    <ChevronDown className={cn("admin-sidebar__chevron", childActive && "admin-sidebar__chevron--open")} />
                                </button>
                                {item.children && childActive ? renderNav(item.children, true) : null}
                            </div>
                        )}
                    </li>
                )
            })}
        </ul>
    )

    if (accessPolicy.isLoading) {
        return <Loading />
    }

    const currentPathAllowed = isPathAllowed(location.pathname, accessPolicy)
    const canOpenSettings = isPathAllowed("/settings", accessPolicy)
    const isDeliveryCenterPage = location.pathname.startsWith("/delivery-center")
    const isAddressCheckPage = location.pathname.startsWith("/address-check")
    const isMapWorkspacePage = isDeliveryCenterPage || isAddressCheckPage

    const renderSidebarContent = (compact = false) => (
        <div className={cn("admin-sidebar", compact && "admin-sidebar--compact")}>
            <div className="admin-sidebar__brand">
                <BrandLogo
                    variant={app.theme === "dark" ? "light" : "dark"}
                    height={compact ? 38 : 35}
                    markOnly={compact}
                    className={compact ? "admin-sidebar__mini-logo" : undefined}
                />
            </div>
            <div className="admin-sidebar__scroll">
                {renderNav(navItems)}
            </div>
            <div className="admin-sidebar__footer">
                <a
                    className="admin-sidebar__utility admin-sidebar__help"
                    href="https://t.me/desli_client_intake_bot"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setMobileOpen(false)}
                >
                    <SidebarHelpIcon />
                    <span className="admin-sidebar__help-copy">
                        <span className="admin-sidebar__help-title">{t("common.help")}</span>
                        <span className="admin-sidebar__help-subtitle">Telegram</span>
                    </span>
                    <SidebarExternalIcon />
                </a>
                {canOpenSettings ? (
                    <button
                        type="button"
                        className={cn(
                            "admin-sidebar__utility admin-sidebar__settings",
                            location.pathname.startsWith("/settings") && "admin-sidebar__utility--active"
                        )}
                        onClick={() => navigateFromSidebar("/settings")}
                    >
                        <SidebarSettingsIcon />
                        <span>{t("navigation.settings")}</span>
                    </button>
                ) : null}
                <button type="button" className="admin-sidebar__utility admin-sidebar__logout" onClick={() => logoutAction({})}>
                    <SidebarLogoutIcon />
                    <span>{t("common.logout")}</span>
                </button>
                <div className="admin-sidebar__version">
                    {t("common.bordikEcosystem")}{" "}
                    <a className="admin-sidebar__version-link" href="https://desli.uz/" target="_blank" rel="noreferrer">
                        Desli
                    </a>
                    .
                </div>
            </div>
        </div>
    )

    return (
        <div className="app-shell min-h-screen text-foreground">
            <aside className={cn("fixed inset-y-0 left-0 z-30 hidden lg:block", isMapWorkspacePage ? "w-[84px]" : "w-72")}>
                {renderSidebarContent(isMapWorkspacePage)}
            </aside>

            {mobileOpen && (
                <div className="fixed inset-0 z-50 bg-foreground/45 lg:hidden" onClick={() => setMobileOpen(false)}>
                    <aside
                        className="h-full w-72 max-w-[calc(100vw-24px)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {renderSidebarContent(false)}
                    </aside>
                </div>
            )}

            <div className={cn(isMapWorkspacePage ? "lg:pl-[84px]" : "lg:pl-72")}>
                {!isMapWorkspacePage ? <header className="app-header sticky top-0 z-20">
                    <div className="app-header__inner flex h-[72px] items-center gap-3 px-4 sm:px-5 lg:px-7">
                        <Button variant="outline" size="icon" className="app-header-icon-button lg:hidden" onClick={() => setMobileOpen(true)}>
                            <Menu className="h-4 w-4" />
                        </Button>

                        <div className="hidden flex-1 xl:flex">
                            <div className="app-header__lookup flex w-full items-center gap-2 rounded-md border border-border bg-card px-2 py-1 shadow-sm">
                                {accessPolicy.canSelectBranches ? (
                                    <div
                                        className="flex min-w-[180px] max-w-[220px] items-center gap-2 px-2 py-1"
                                    >
                                        <span
                                            className={cn(
                                                "app-header__mode-icon flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-transparent bg-secondary text-muted-foreground transition-colors",
                                                hasSelectedBranch && "app-header__mode-icon--active border-primary/25 bg-primary/10 text-primary"
                                            )}
                                        >
                                            <Store className="h-3.5 w-3.5" />
                                        </span>
                                        <Select
                                            allowClear
                                            showSearch
                                            loading={isBranchesLoading}
                                            value={selectedBranchId}
                                            placeholder={t("common.allBranches")}
                                            optionFilterProp="label"
                                            variant="borderless"
                                            suffixIcon={null}
                                            className="header-branch-select"
                                            options={branchOptions}
                                            onChange={(value) => dispatch(setSelectedBranchId(value))}
                                        />
                                    </div>
                                ) : null}
                                {accessPolicy.canSelectBranches && accessPolicy.canReadOrders ? <div className="h-5 w-px bg-border/80" /> : null}
                                {accessPolicy.canReadOrders ? (
                                    <div
                                        className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1"
                                    >
                                        <span
                                            className={cn(
                                                "app-header__mode-icon flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-transparent bg-secondary text-muted-foreground transition-colors",
                                                hasQuickLookup && "app-header__mode-icon--active border-primary/25 bg-primary/10 text-primary"
                                            )}
                                        >
                                            <Search className="h-3.5 w-3.5" />
                                        </span>
                                        <Input
                                            className="h-7 border-0 bg-transparent px-0 py-0 text-sm shadow-none outline-none placeholder:text-muted-foreground/80 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                            placeholder={quickLookupPlaceholder}
                                            value={quickLookupValue}
                                            onChange={(event) => setQuickLookupValue(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault()
                                                    submitQuickLookup()
                                                }
                                            }}
                                        />
                                        {hasQuickLookup ? (
                                            <button
                                                type="button"
                                                aria-label={t("common.clearSearch")}
                                                title={t("common.clearSearch")}
                                                onClick={clearQuickLookup}
                                                className="app-header__clear-button flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={submitQuickLookup}
                                            className="app-header__search-button shrink-0 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-primary/10"
                                        >
                                            {t("common.search")}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className="ml-auto flex items-center gap-1.5">
                            <div className="hidden items-center gap-1.5 xl:flex">
                                {accessPolicy.canReadOrders ? <Tooltip title={t("header.activeOrdersHint")}>
                                    <button
                                        type="button"
                                        onClick={() => push("/orders")}
                                        className="app-header-live app-header-live--orders group"
                                    >
                                        <span
                                            className={cn(
                                                "flex h-10 items-center gap-2 rounded-lg border px-2.5 shadow-sm transition-colors",
                                                "border-amber-500/25 bg-card",
                                                "hover:border-amber-500/45 hover:bg-amber-500/10"
                                            )}
                                        >
                                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-500/15 text-amber-500">
                                                <ReceiptText className="h-3.5 w-3.5" />
                                            </span>
                                            <span className="text-sm font-semibold leading-none text-foreground">{liveOrdersCount}</span>
                                        </span>
                                    </button>
                                </Tooltip> : null}
                                {accessPolicy.canReadCouriers ? <Tooltip title={t("header.activeCouriersHint")}>
                                    <button
                                        type="button"
                                        onClick={() => push("/settings/couriers")}
                                        className="app-header-live app-header-live--couriers group"
                                    >
                                        <span
                                            className={cn(
                                                "flex h-10 items-center gap-2 rounded-lg border px-2.5 shadow-sm transition-colors",
                                                "border-emerald-500/25 bg-card",
                                                "hover:border-emerald-500/45 hover:bg-emerald-500/10"
                                            )}
                                        >
                                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-500">
                                                <CarFront className="h-3.5 w-3.5" />
                                            </span>
                                            <span className="text-sm font-semibold leading-none text-foreground">{liveCouriersCount}</span>
                                        </span>
                                    </button>
                                </Tooltip> : null}
                            </div>
                            <AdminNotificationsCenter
                                accountId={accessPolicy.accountId}
                                icon={<HeaderNotificationIcon />}
                            />
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setProfileOpen(prev => !prev)}
                                    className="app-header-profile flex h-10 items-center gap-2 rounded-lg border border-border bg-card px-2.5 text-sm transition-colors hover:bg-secondary"
                                >
                                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/15 text-primary">
                                        <User className="h-3.5 w-3.5" />
                                    </span>
                                    <span className="hidden sm:block">{headerUserName}</span>
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                </button>
                                {profileOpen && (
                                    <div className="app-header-profile-menu absolute right-0 z-50 mt-2 w-56 rounded-md border border-border bg-card p-1 shadow-lg">
                                        <button
                                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent"
                                            onClick={() => {
                                                setProfileOpen(false)
                                                push("/profile")
                                            }}
                                        >
                                            <Settings className="h-4 w-4" />
                                            {t("navigation.profile")}
                                        </button>
                                        <button
                                            type="button"
                                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent"
                                            onClick={() => dispatch(changeTheme(app.theme === "dark" ? "light" : "dark"))}
                                        >
                                            {app.theme === "dark" ? <HeaderLightThemeIcon /> : <HeaderDarkThemeIcon />}
                                            {t(app.theme === "dark" ? "common.themeDark" : "common.themeLight")}
                                        </button>
                                        <div className="app-header-profile-menu__language px-3 py-2">
                                            <span className="mb-1.5 block text-xs text-muted-foreground">{t("common.language")}</span>
                                            <LanguageSwitcher compact className="app-header-profile-language" />
                                        </div>
                                        <div className="mx-2 my-1 border-t border-border" />
                                        <button
                                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-500 hover:bg-red-500/10"
                                            onClick={() => {
                                                setProfileOpen(false)
                                                logoutAction({})
                                            }}
                                        >
                                            <LogOut className="h-4 w-4" />
                                            {t("common.exit")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="app-header__mobile-tools xl:hidden">
                        {accessPolicy.canSelectBranches ? (
                            <div className="app-header__mobile-control app-header__mobile-control--branch">
                                <span
                                    className={cn(
                                        "app-header__mode-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-secondary text-muted-foreground transition-colors",
                                        hasSelectedBranch && "app-header__mode-icon--active border-primary/25 bg-primary/10 text-primary"
                                    )}
                                >
                                    <Store className="h-4 w-4" />
                                </span>
                                <Select
                                    allowClear
                                    showSearch
                                    loading={isBranchesLoading}
                                    value={selectedBranchId}
                                    placeholder={t("common.allBranches")}
                                    optionFilterProp="label"
                                    variant="borderless"
                                    suffixIcon={null}
                                    className="header-branch-select"
                                    options={branchOptions}
                                    onChange={(value) => dispatch(setSelectedBranchId(value))}
                                />
                            </div>
                        ) : null}
                        {accessPolicy.canReadOrders ? (
                            <div className="app-header__mobile-control app-header__mobile-control--search">
                                <span
                                    className={cn(
                                        "app-header__mode-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-secondary text-muted-foreground transition-colors",
                                        hasQuickLookup && "app-header__mode-icon--active border-primary/25 bg-primary/10 text-primary"
                                    )}
                                >
                                    <Search className="h-4 w-4" />
                                </span>
                                <Input
                                    className="h-8 min-w-0 flex-1 border-0 bg-transparent px-0 py-0 text-sm shadow-none outline-none placeholder:text-muted-foreground/80 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                    placeholder={quickLookupPlaceholder}
                                    value={quickLookupValue}
                                    onChange={(event) => setQuickLookupValue(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault()
                                            submitQuickLookup()
                                        }
                                    }}
                                />
                                {hasQuickLookup ? (
                                    <button
                                        type="button"
                                        aria-label={t("common.clearSearch")}
                                        title={t("common.clearSearch")}
                                        onClick={clearQuickLookup}
                                        className="app-header__clear-button flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    onClick={submitQuickLookup}
                                    className="app-header__search-button shrink-0 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-primary/10"
                                >
                                    {t("common.search")}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </header> : null}

                <main className={cn(
                    "app-main min-h-[calc(100vh-4.5rem)]",
                    isDeliveryCenterPage
                        ? "app-main--delivery-center p-0"
                        : isAddressCheckPage
                            ? "app-main--address-check p-0"
                            : "p-4 sm:p-6 lg:p-8"
                )}>
                    {currentPathAllowed ? children : (
                        <Result
                            status="403"
                            title={t("errors.noAccess")}
                            subTitle={t("errors.noPermissionForSection")}
                        />
                    )}
                </main>
            </div>
        </div>
    )
}
